"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import Confetti from "react-confetti";
import retardioImage from "./images/radbro.png";
import schizoImage from "./images/schizo.png";
import sprotoImage from "./images/sproto.png";
import germanImage from "./images/german.png";

const INITIAL_IMAGES = [retardioImage, schizoImage, sprotoImage, germanImage];
const EXTENDED_IMAGES = INITIAL_IMAGES;
const INITIAL_CREDITS = 100;
const SPIN_COST = 1;

const PRIZES = {
  [retardioImage]: { name: "Retardio", value: 10 },
  [schizoImage]: { name: "Schizoposter", value: 5 },
  [sprotoImage]: { name: "Sproto Gremlin", value: 15 },
  [germanImage]: { name: "Hitler", value: -10 },
};

const TetrisBlock = ({ lit }) => (
  <div
    className={`w-4 h-4 border border-gray-700 ${
      lit ? "bg-blue-500" : "bg-gray-800"
    } transition-colors duration-100`}
  ></div>
);

const TetrisBackground = () => {
  const [grid, setGrid] = useState(
    Array(20)
      .fill()
      .map(() => Array(10).fill(false))
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setGrid((prevGrid) => {
        const newGrid = prevGrid.map((row) => [...row]);
        for (let i = 0; i < 10; i++) {
          const row = Math.floor(Math.random() * 20);
          const col = Math.floor(Math.random() * 10);
          newGrid[row][col] = !newGrid[row][col];
        }
        return newGrid;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute inset-0 grid grid-cols-10 gap-px p-2 opacity-25">
      {grid.flat().map((lit, index) => (
        <TetrisBlock key={index} lit={lit} />
      ))}
    </div>
  );
};

const CandlestickChart = ({ isSpinning, outcome }) => {
  const canvasRef = useRef(null);
  const [data, setData] = useState([]);
  const [volatility, setVolatility] = useState(1);

  useEffect(() => {
    if (isSpinning) {
      setData([]);
      setVolatility(1);
      const interval = setInterval(() => {
        setData((prevData) => {
          const lastClose = prevData.length
            ? prevData[prevData.length - 1].close
            : 50;
          const range = 20 * volatility;
          const change = (Math.random() - 0.5) * range;
          const open = Math.max(0, Math.min(100, lastClose));
          const close = Math.max(0, Math.min(100, open + change));
          const high = Math.max(
            open,
            close,
            Math.min(100, Math.max(open, close) + (Math.random() * range) / 2)
          );
          const low = Math.min(
            open,
            close,
            Math.max(0, Math.min(open, close) - (Math.random() * range) / 2)
          );
          return [...prevData, { open, close, high, low }].slice(-20);
        });
        // setVolatility((prev) => Math.min(3, prev + 0.1));
        setVolatility(1);
      }, 250);

      return () => clearInterval(interval);
    }
  }, [isSpinning]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (data.length < 2) return;

    // Draw gridlines
    ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
    ctx.lineWidth = 1;

    // Vertical gridlines
    for (let i = 0; i <= 20; i++) {
      const x = (i / 20) * canvas.width;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }

    // Horizontal gridlines
    for (let i = 0; i <= 10; i++) {
      const y = (i / 10) * canvas.height;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    const drawCandle = (candle, x, width) => {
      const scaleY = canvas.height / 100;

      ctx.fillStyle = candle.close > candle.open ? "green" : "red";
      ctx.strokeStyle = ctx.fillStyle;

      // Calculate positions
      const highY = canvas.height - candle.high * scaleY;
      const lowY = canvas.height - candle.low * scaleY;
      const openY = canvas.height - candle.open * scaleY;
      const closeY = canvas.height - candle.close * scaleY;

      // Draw the wick
      ctx.beginPath();
      ctx.moveTo(x + width / 2, highY);
      ctx.lineTo(x + width / 2, lowY);
      ctx.stroke();

      // Draw the body
      const bodyStart = Math.min(openY, closeY);
      const bodyHeight = Math.abs(closeY - openY);
      ctx.fillRect(x, bodyStart, width, bodyHeight || 1);
    };

    const candleWidth = canvas.width / 20;

    const displayData = [...data];

    if (!isSpinning && outcome) {
      const lastCandle = displayData[displayData.length - 1];
      const newCandle = { ...lastCandle };
      if (outcome === "win") {
        newCandle.close = 100;
        newCandle.high = 100;
      } else if (outcome === "lose") {
        newCandle.close = 0;
        newCandle.low = 0;
      }
      displayData[displayData.length - 1] = newCandle;
    }

    displayData.forEach((candle, index) => {
      drawCandle(candle, index * candleWidth, candleWidth - 1);
    });
  }, [data, isSpinning, outcome]);

  return (
    <canvas
      ref={canvasRef}
      width="300"
      height="200"
      className="w-full h-48 bg-gray-700"
    />
  );
};

export default function TetrisSlotMachine() {
  const [credits, setCredits] = useState(INITIAL_CREDITS);
  const [slots, setSlots] = useState(() =>
    Array(3)
      .fill(null)
      .map(
        () => INITIAL_IMAGES[Math.floor(Math.random() * INITIAL_IMAGES.length)]
      )
  );
  const [images, setImages] = useState(INITIAL_IMAGES);
  const [spinning, setSpinning] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [prize, setPrize] = useState("");
  const [leverPosition, setLeverPosition] = useState("up");
  const [spinCount, setSpinCount] = useState(0);
  const [chartOutcome, setChartOutcome] = useState(null);
  const audioContextRef = useRef(null);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext ||
      window.webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playSound = useCallback((frequency, duration) => {
    if (audioContextRef.current) {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      oscillator.frequency.setValueAtTime(
        frequency,
        audioContextRef.current.currentTime
      );
      gainNode.gain.setValueAtTime(0.1, audioContextRef.current.currentTime);

      oscillator.start();
      oscillator.stop(audioContextRef.current.currentTime + duration);
    }
  }, []);

  const spin = () => {
    if (credits < SPIN_COST) return;

    setCredits(credits - SPIN_COST);
    setSpinning(true);
    setPrize("");
    setLeverPosition("down");
    setChartOutcome(null);

    const spinDuration = 5000;
    const intervalDuration = 100;
    let elapsed = 0;

    const spinInterval = setInterval(() => {
      setSlots(
        slots.map(() => images[Math.floor(Math.random() * images.length)])
      );
      playSound(440 + Math.random() * 220, 0.05);
      elapsed += intervalDuration;

      if (elapsed >= spinDuration) {
        clearInterval(spinInterval);
        setSpinning(false);
        setLeverPosition("up");
        const finalSlots = generateWinningCombination();
        setSlots(finalSlots);
        checkWin(finalSlots);
        setSpinCount((prevCount) => prevCount + 1);
      }
    }, intervalDuration);
  };

  useEffect(() => {
    if (spinCount === 1) {
      setImages(EXTENDED_IMAGES);
    }
  }, [spinCount]);

  const generateWinningCombination = () => {
    const randomNumber = Math.random();
    if (randomNumber < 0.5) {
      // 50% chance to win
      const winningImage = images[Math.floor(Math.random() * images.length)];
      return [winningImage, winningImage, winningImage];
    } else {
      return Array(3)
        .fill(null)
        .map(() => images[Math.floor(Math.random() * images.length)]);
    }
  };

  const checkWin = (finalSlots) => {
    if (new Set(finalSlots).size === 1) {
      const winningSymbol = finalSlots[0];
      const wonPrize = PRIZES[winningSymbol];
      setCredits((credits) => credits + wonPrize.value);
      setPrize(wonPrize.name);
      if (winningSymbol === germanImage) {
        setChartOutcome("lose");
        playSound(110, 0.5); // Penalty sound
      } else {
        setChartOutcome("win");
        setShowConfetti(true);
        playSound(880, 0.5); // Win sound
      }
    } else {
      setChartOutcome("neutral");
      playSound(220, 0.3); // Lose sound
    }
  };

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900 p-4">
      <div className="relative w-full max-w-md bg-gray-800 border-4 border-gray-600 p-4 shadow-lg overflow-hidden">
        <TetrisBackground />
        <div className="relative z-10">
          <div className="bg-gray-700 text-white p-2 mb-4 flex items-center justify-between">
            <span className="font-bold text-xl">Caddi Slot Machine</span>
            <span className="text-xl">Credits: {credits}</span>
          </div>
          <div className="bg-gray-600 p-4 mb-4">
            <div className="flex justify-center space-x-4 mb-4">
              {slots.map((slot, index) => (
                <div
                  key={index}
                  className="w-20 h-20 bg-white border-2 border-gray-400 flex items-center justify-center"
                >
                  <img
                    src={slot}
                    alt="Slot"
                    className="w-16 h-16 object-contain"
                  />
                </div>
              ))}
            </div>
            {prize && (
              <div
                className={`text-center text-xl mt-2 ${
                  prize === "Hitler" ? "text-red-600" : "text-green-400"
                }`}
              >
                {prize === "Hitler"
                  ? "You lost: Hitler!"
                  : `You won: ${prize}!`}
              </div>
            )}
          </div>
          <CandlestickChart isSpinning={spinning} outcome={chartOutcome} />
          <div className="flex items-center mt-4">
            <button
              onClick={spin}
              disabled={spinning || credits < SPIN_COST}
              className="flex-grow bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {spinning ? "Spinning..." : `Spin (${SPIN_COST} credit)`}
            </button>
            <div className="ml-4 w-8 h-32">
              <div
                className={`w-6 h-24 bg-gray-300 rounded-t-full rounded-b-full relative overflow-hidden transition-transform duration-300 ${
                  leverPosition === "down" ? "translate-y-6" : ""
                }`}
              >
                <div className="w-full h-1/2 bg-red-500 absolute top-0 rounded-t-full"></div>
                <div className="w-full h-1/2 bg-gray-400 absolute bottom-0 rounded-b-full"></div>
              </div>
            </div>
          </div>
          <div className="bg-gray-600 p-4 mt-4">
            <h3 className="text-center font-bold mb-2 text-white">Prizes</h3>
            <ul className="text-sm grid grid-cols-2 gap-2">
              {Object.entries(PRIZES).map(([image, { name }]) => (
                <li key={image} className="flex justify-between items-center">
                  <span className="text-white flex items-center">
                    <img
                      src={image}
                      alt={name}
                      className="w-6 h-6 mr-2 object-contain"
                    />
                    {name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {showConfetti && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={200}
          />
        </div>
      )}
    </div>
  );
}
